interface LedgerPledge {
    id: string
    amount: number
    closedAt?: string
    user?: {
        id?: string | null
        givenName?: string | null
        familyName?: string | null
        imageUrl?: string | null
    }
    givenName?: string | null
    familyName?: string | null
    name?: string | null
}

interface LedgerBreakdownItem {
    amount: number
    familyName?: string | null
    givenName?: string | null
    id: string
    imageUrl?: string | null
    userId?: string | null
}

interface LedgerTransaction {
    amount: number
    status: string
}

interface LedgerLineItemShape {
    breakdown: LedgerBreakdownItem[]
    transactionId: string
}

interface LedgerGroupShape {
    pledges?: LedgerPledge[]
    transactions?: Record<string, LedgerTransaction[]>
}

export type LedgerHelperGroup = LedgerGroupShape

export interface LedgerEditorSlot extends LedgerBreakdownItem {
    isManuallyUpdated: boolean
}

const emptyDate = '0001-01-01T00:00:00Z'

export const isPledgeClosed = (pledge?: LedgerPledge) => !!pledge?.closedAt && pledge.closedAt !== emptyDate

export const getTransactionSpend = (relatedTransactions?: LedgerTransaction[]) => {
    let amount = 0
    const latestTransaction = relatedTransactions?.at(-1)

    if (latestTransaction && (latestTransaction.status === 'pending' || latestTransaction.status === 'finalized' || latestTransaction.status === 'Credit')) {
        if (latestTransaction.status === 'Credit') {
            amount = (relatedTransactions?.find(transaction => transaction.status === 'finalized')?.amount || 0) - latestTransaction.amount

            if (amount < 0) amount = 0
        } else {
            amount = latestTransaction.amount
        }
    }

    return amount
}

export const sumBreakdown = (breakdown: LedgerBreakdownItem[] = []) => breakdown.reduce((sum, item) => sum + item.amount, 0)

export const buildEvenSplitSlots = (slots: LedgerEditorSlot[], spendAmount: number, pledges?: LedgerPledge[]) => {
    if (!spendAmount || slots.length === 0) return slots

    const closedSlots = slots.filter(slot => isPledgeClosed(pledges?.find(pledge => pledge.id === slot.id)))
    const closedAmount = closedSlots.reduce((sum, slot) => sum + slot.amount, 0)
    const activeSpendAmount = Math.max(0, spendAmount - closedAmount)
    const activeSlots = slots.filter(slot => !isPledgeClosed(pledges?.find(pledge => pledge.id === slot.id)))

    if (activeSlots.length === 0) return slots

    const perSlotAmount = Math.floor(activeSpendAmount / activeSlots.length)
    const remainder = activeSpendAmount - (perSlotAmount * activeSlots.length)
    const highestPledgeAmount = Math.max(...(pledges?.map(pledge => pledge.amount) || [0]))
    const highestPledgers = pledges?.filter(pledge => pledge.amount === highestPledgeAmount && activeSlots.some(slot => slot.id === pledge.id)) || []
    const targetPledge = highestPledgers[0] || activeSlots[0]

    return slots.map(slot => {
        if (!activeSlots.some(activeSlot => activeSlot.id === slot.id)) return slot

        return {
            ...slot,
            amount: perSlotAmount + (slot.id === targetPledge.id ? remainder : 0),
        }
    })
}

export const buildWeightedSplitSlots = (slots: LedgerEditorSlot[], spendAmount: number, pledges?: LedgerPledge[]) => {
    if (!spendAmount || slots.length === 0) return slots

    const activeSlots = slots.filter(slot => !isPledgeClosed(pledges?.find(pledge => pledge.id === slot.id)))
    if (activeSlots.length === 0) return slots

    const totalPledgeAmount = slots.reduce((sum, slot) => sum + (pledges?.find(pledge => pledge.id === slot.id)?.amount || 0), 0)
    if (totalPledgeAmount === 0) return buildEvenSplitSlots(slots, spendAmount, pledges)

    const weightedSlots = slots.map(slot => {
        if (!activeSlots.some(activeSlot => activeSlot.id === slot.id)) return slot

        const pledgeAmount = pledges?.find(pledge => pledge.id === slot.id)?.amount || 0
        const share = Math.floor((pledgeAmount / totalPledgeAmount) * spendAmount)
        return { ...slot, amount: share }
    })

    const remainder = spendAmount - weightedSlots.reduce((sum, slot) => sum + slot.amount, 0)
    if (remainder === 0) return weightedSlots

    const activeTotalPledge = activeSlots.reduce((sum, slot) => sum + (pledges?.find(pledge => pledge.id === slot.id)?.amount || 0), 0)
    if (activeTotalPledge <= 0) return weightedSlots

    let distributedRemainder = 0
    const adjustedSlots = weightedSlots.map(slot => {
        if (!activeSlots.some(activeSlot => activeSlot.id === slot.id)) return slot

        const pledgeAmount = pledges?.find(pledge => pledge.id === slot.id)?.amount || 0
        const exactShare = (pledgeAmount / activeTotalPledge) * remainder
        const intShare = Math.trunc(exactShare)
        distributedRemainder += intShare
        return { ...slot, amount: slot.amount + intShare }
    })

    const finalRemainder = remainder - distributedRemainder
    if (finalRemainder === 0) return adjustedSlots

    const highestPledgeAmount = Math.max(...(activeSlots.map(slot => pledges?.find(pledge => pledge.id === slot.id)?.amount || 0)))
    const highestPledgers = activeSlots.filter(slot => (pledges?.find(pledge => pledge.id === slot.id)?.amount || 0) === highestPledgeAmount)
    const targetId = highestPledgers[0]?.id || activeSlots[0].id

    return adjustedSlots.map(slot => slot.id === targetId ? { ...slot, amount: slot.amount + finalRemainder } : slot)
}

export const buildContributionSlots = (group: LedgerGroupShape, sortedLedger: LedgerLineItemShape[]) => {
    const totalsByPledgeId = sortedLedger.reduce<Record<string, number>>((acc, item) => {
        item.breakdown.forEach(entry => {
            acc[entry.id] = (acc[entry.id] || 0) + entry.amount
        })
        return acc
    }, {})

    return (group.pledges || []).map(pledge => ({
        id: pledge.id,
        userId: pledge.user?.id,
        givenName: pledge.user?.givenName || pledge.givenName || pledge.name,
        familyName: pledge.user?.familyName || pledge.familyName,
        imageUrl: pledge.user?.imageUrl,
        amount: totalsByPledgeId[pledge.id] || 0,
        isManuallyUpdated: false,
    }))
}

export const getContributionOverMaxUserIds = (slots: LedgerEditorSlot[], pledges?: LedgerPledge[]) => {
    return slots.reduce<string[]>((acc, slot) => {
        const pledge = pledges?.find(item => item.id === slot.id)
        if (!!pledge && slot.amount > pledge.amount && slot.userId) acc.push(slot.userId)
        return acc
    }, [])
}

export const buildLedgerFromContributionTotals = ({
    group,
    sortedLedger,
    contributionSlots,
}: {
    group: LedgerGroupShape
    sortedLedger: LedgerLineItemShape[]
    contributionSlots: LedgerEditorSlot[]
}) => {
    const remainingByPledgeId = contributionSlots.reduce<Record<string, number>>((acc, slot) => {
        acc[slot.id] = Math.max(0, Math.round(slot.amount))
        return acc
    }, {})

    const nextLedger = sortedLedger.map(item => {
        const transactionSpend = getTransactionSpend(group.transactions?.[item.transactionId])
        let remainingTransactionSpend = transactionSpend

        const nextBreakdown = item.breakdown.map(entry => {
            const nextAmount = Math.min(remainingByPledgeId[entry.id] || 0, remainingTransactionSpend)
            remainingByPledgeId[entry.id] = Math.max(0, (remainingByPledgeId[entry.id] || 0) - nextAmount)
            remainingTransactionSpend -= nextAmount

            return { ...entry, amount: nextAmount }
        })

        if (remainingTransactionSpend > 0) {
            const fallbackIndex = nextBreakdown.findIndex(entry => !isPledgeClosed(group.pledges?.find(pledge => pledge.id === entry.id)))
            if (fallbackIndex > -1) {
                nextBreakdown[fallbackIndex] = {
                    ...nextBreakdown[fallbackIndex],
                    amount: nextBreakdown[fallbackIndex].amount + remainingTransactionSpend,
                }
                remainingByPledgeId[nextBreakdown[fallbackIndex].id] = Math.max(0, (remainingByPledgeId[nextBreakdown[fallbackIndex].id] || 0) - remainingTransactionSpend)
            }
        }

        return {
            ...item,
            breakdown: nextBreakdown,
        }
    })

    return nextLedger
}
